/**
 * Client-side tile-query cache/dedupe (amendments §5).
 *
 * Two tiles on one dashboard that resolve to the identical query + window
 * (e.g. both reference the same saved exploration) should cost ONE request,
 * not two — and a re-render inside the scrape interval shouldn't re-fetch at
 * all. Keyed by the caller-built key (query body + window), TTL ≈ the scrape
 * interval. The in-flight promise itself is cached, so concurrent identical
 * fetches coalesce; a rejected fetch is evicted immediately (errors are never
 * served from cache).
 */

import { SCRAPE_INTERVAL_MS } from './metric-exploration.js';

interface Entry {
  at: number;
  promise: Promise<unknown>;
}

const cache = new Map<string, Entry>();

/** Safety bound — a dashboard has ≤24 tiles, so this never triggers in normal
 * use; it just keeps a pathological key churn from growing without limit. */
const MAX_ENTRIES = 200;

export function cachedTileQuery<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs: number = SCRAPE_INTERVAL_MS,
): Promise<T> {
  const now = Date.now();
  const hit = cache.get(key);
  if (hit && now - hit.at < ttlMs) return hit.promise as Promise<T>;
  const promise = fetcher();
  cache.set(key, { at: now, promise });
  promise.catch(() => {
    if (cache.get(key)?.promise === promise) cache.delete(key);
  });
  if (cache.size > MAX_ENTRIES) {
    // Evict oldest-first until back under the bound.
    const entries = [...cache.entries()].sort((a, b) => a[1].at - b[1].at);
    for (const [k] of entries.slice(0, cache.size - MAX_ENTRIES)) cache.delete(k);
  }
  return promise;
}

/** Test hook / hard reset. */
export function clearTileQueryCache(): void {
  cache.clear();
}
